import Button from "@atomaro/ui-kit/components/Button/Button/Button";
import {FC, useEffect, useState} from "react";
import {COLORS} from "shared/consts";
import {ButtonWrapper, Separator} from "./styles";
import {DiaryCustomFields} from "../diaryCustomFeilds/ui/DiaryCustomFields";
import {DiaryRow} from "./types";
import {toRem} from "shared/helpers";
import {DropdownMenu, IconButton} from "@atomaro/ui-kit";
import {ColItem} from "entities/diary/model/types";

type DiaryEditHeaderProps = {
  onChange: (activeCols: ColItem[]) => void;
  items: ColItem[];
  customFields?: ColItem[];
  randomRows?: DiaryRow;
};

export const DiaryEditHeader: FC<DiaryEditHeaderProps> = ({
  onChange,
  items,
  customFields,
  randomRows,
}) => {
  const [isOpened, setIsOpened] = useState(false);
  const [localItems, setLocalItems] = useState<ColItem[]>(items);
  const [customItems, setCustomItems] = useState<ColItem[]>(customFields ?? []);
  useEffect(() => {
    setLocalItems(items);
  }, [items]);

  const selectedKeys = localItems.filter((i) => i.isSelected).map((i) => i.key);

  const handleChange = (keys: (string | number)[]) => {
    setLocalItems((prev) =>
      prev.map((el) => ({
        ...el,
        isSelected: keys.includes(el.key),
      }))
    );
  };

  const handleChangeCustom = (id: string) => {
    setCustomItems((prev) =>
      prev.map((el) =>
        el.key === id ? { ...el, isSelected: !el.isSelected } : el
      )
    );
  };

  const handleApply = () => {
    onChange([...localItems, ...customItems]);
    setIsOpened(false);
  };

  return (
    <DropdownMenu
      onClose={() => {
        setIsOpened(false);
        setLocalItems(items);
        setCustomItems(customFields ?? []);
      }}
      isOpened={isOpened}
      isMultiselect
      hideOnSelect={false}
      value={selectedKeys}
      placement="bottomLeft"
      style={{
        background: COLORS.BG.SURFACE2,
        width: toRem(240),
      }}
      items={items}
      onChange={handleChange}
      footerSlot={() => (
        <>
          {customFields?.length !== 0 && <Separator />}
          <DiaryCustomFields
            isSettings
            customFieldsCol={customItems}
            randomRows={randomRows}
            handleChangeCustom={handleChangeCustom}
          />
          <Separator />
          <ButtonWrapper>
            <Button
              label={"Применить"}
              size="m"
              variant="primary"
              colorScheme="accent"
              style={{ width: "100%" }}
              onClick={() => {
                handleApply();
              }}
            />
          </ButtonWrapper>
        </>
      )}
    >
      <IconButton
        onClick={() => {
          setIsOpened((prev) => !prev);
          setLocalItems(items);
          setCustomItems(customFields ?? []);
        }}
        icon={
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M6.84206 2.33332L9.15663 2.33332L9.4616 3.3973C9.6843 4.17424 10.4825 4.63504 11.2667 4.43936L12.3479 4.16956L13.5024 6.16269L12.7331 6.95524C12.1682 7.5372 12.1682 8.46278 12.7331 9.04474L13.5024 9.8373L12.3479 11.8304L11.2667 11.5606C10.4825 11.3649 9.68431 11.8257 9.46161 12.6027L9.15663 13.6667L6.84207 13.6667L6.53709 12.6027C6.31439 11.8258 5.51617 11.365 4.73199 11.5606L3.65076 11.8304L2.49631 9.83731L3.26561 9.04475C3.8305 8.46278 3.8305 7.53721 3.26561 6.95525L2.49631 6.16268L3.65075 4.16955L4.73198 4.43936C5.51616 4.63504 6.31439 4.17423 6.53709 3.3973L6.84206 2.33332ZM11.0246 3.46912C10.7632 3.53434 10.4971 3.38074 10.4229 3.12176L9.97948 1.57481C9.9385 1.43184 9.80777 1.33332 9.65905 1.33332L6.33964 1.33332C6.19092 1.33332 6.06019 1.43184 6.01921 1.57481L5.5758 3.12175C5.50156 3.38073 5.23549 3.53433 4.97409 3.46911L3.40605 3.07783C3.26191 3.04186 3.11137 3.10562 3.03691 3.23417L1.37815 6.09798C1.30337 6.22709 1.32349 6.39016 1.42741 6.49722L2.54806 7.65175C2.73636 7.84573 2.73636 8.15426 2.54806 8.34825L1.42741 9.50277C1.32349 9.60983 1.30337 9.77291 1.37815 9.90201L3.03691 12.7658C3.11137 12.8944 3.26191 12.9581 3.40605 12.9222L4.9741 12.5309C5.23549 12.4657 5.50157 12.6193 5.57581 12.8782L6.01921 14.4252C6.06019 14.5681 6.19092 14.6667 6.33964 14.6667L9.65905 14.6667C9.80777 14.6667 9.9385 14.5681 9.97948 14.4252L10.4229 12.8782C10.4971 12.6192 10.7632 12.4656 11.0246 12.5309L12.5926 12.9221C12.7368 12.9581 12.8873 12.8944 12.9618 12.7658L14.6205 9.902C14.6953 9.77289 14.6752 9.60982 14.5713 9.50276L13.4506 8.34824C13.2623 8.15425 13.2623 7.84573 13.4506 7.65174L14.5713 6.49722C14.6752 6.39017 14.6953 6.22709 14.6205 6.09799L12.9618 3.23418C12.8873 3.10563 12.7368 3.04187 12.5926 3.07784L11.0246 3.46912ZM7.99992 6.33332C8.92039 6.33332 9.66659 7.07952 9.66659 7.99999C9.66659 8.92046 8.92039 9.66666 7.99992 9.66666C7.07944 9.66666 6.33325 8.92046 6.33325 7.99999C6.33325 7.07952 7.07944 6.33332 7.99992 6.33332ZM7.99992 5.33332C9.47268 5.33332 10.6666 6.52723 10.6666 7.99999C10.6666 9.47275 9.47268 10.6667 7.99992 10.6667C6.52716 10.6667 5.33325 9.47275 5.33325 7.99999C5.33325 6.52723 6.52716 5.33332 7.99992 5.33332Z"
              fill={COLORS.NEUTRAL.MUTED}
            />
          </svg>
        }
        colorScheme="neutral"
        variant="ghost"
      />
    </DropdownMenu>
  );
};
